/**
 * Referring-clinician view — simplified worklist of "their" patients.
 * Read-only: no measurement, no report drafting. Report-centric.
 *
 * Scope narrowing (patient MRNs) is enforced on the backend by the
 * X-VaultPod-Claims header; this page just renders what comes back.
 */
import { createSignal, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { A } from "@solidjs/router";
import { listStudies, type Study } from "../api/client";

export function ClinicianPage() {
  const [q, setQ] = createSignal("");
  const studies = createQuery(() => ({
    queryKey: ["studies", "referrals", q()],
    queryFn:  () => listStudies(q() ? { PatientName: q() } : {}),
  }));

  return (
    <div class="flex h-full flex-col">
      <header class="flex flex-wrap items-center gap-3 border-b border-ink-300 bg-ink-100 px-4 py-3">
        <span class="chip-brand">My patients</span>
        <span class="text-xs text-ink-700">
          Reports and key images for patients you referred.
        </span>
        <div class="min-w-[240px] flex-1">
          <input type="search"
                 placeholder="Patient name, MRN…"
                 class="w-full rounded border border-ink-300 bg-ink-200 px-3 py-1.5 text-sm"
                 value={q()}
                 onInput={(e) => setQ(e.currentTarget.value)} />
        </div>
      </header>

      <div class="flex-1 overflow-auto p-4">
        <Show when={studies.isLoading} fallback={null}>
          <div class="p-6 text-center text-ink-700">loading…</div>
        </Show>
        <Show when={studies.isError}>
          <div class="rounded border border-danger/50 bg-danger/10 p-3 text-sm text-danger">
            Unable to load your patient list — {String(studies.error)}
          </div>
        </Show>
        <Show when={studies.data && studies.data.length === 0}>
          <div class="mx-auto max-w-md rounded-lg border border-ink-300 bg-ink-100 p-8 text-center">
            <div class="text-lg font-semibold">No imaging for your patients yet.</div>
            <div class="mt-2 text-sm text-ink-700">
              Studies referred by you will appear here as the reports are
              signed by the reading radiologist.
            </div>
          </div>
        </Show>
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          <For each={studies.data ?? []}>
            {(s: Study) => (
              <A href={`/study/${s.studyUid}`}
                 class="rounded-lg border border-ink-300 bg-ink-100 p-4 shadow-panel transition hover:border-brand">
                <div class="flex items-start justify-between gap-2">
                  <div>
                    <div class="font-semibold text-white">{s.patientName}</div>
                    <div class="font-mono text-[11px] text-ink-700">MRN {s.patientId}</div>
                  </div>
                  <span class="chip-brand">{s.modality}</span>
                </div>
                <div class="mt-2 text-xs text-ink-700">
                  {fmtDate(s.studyDate)} · {s.description || "—"}
                </div>
                <div class="mt-3 flex items-center justify-between text-[11px]">
                  <span class="text-ink-700">{s.instanceCount} images</span>
                  <Show when={s.reportSigned}
                        fallback={<span class="text-warn">report pending</span>}>
                    <span class="text-brand">report ready</span>
                  </Show>
                </div>
              </A>
            )}
          </For>
        </div>
      </div>
    </div>
  );
}

function fmtDate(d: string): string {
  if (!d || d.length !== 8) return d;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
}
